// src/products/analysis/services/cryptoService.js
const COINGECKO_BASE_URL = 'https://api.coingecko.com/api/v3';

/**
 * Get top cryptocurrencies by market cap using CoinGecko
 * CoinGecko provides market cap, 7-day change, and all necessary data in one call
 */
export const getTopCryptocurrencies = async (limit = 25) => {
  try {
    // CoinGecko's /coins/markets endpoint returns everything we need in one call
    const response = await fetch(
      `${COINGECKO_BASE_URL}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${limit}&page=1&sparkline=false&price_change_percentage=7d`
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('CoinGecko API rate limit exceeded. Please wait a moment and try again.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('No cryptocurrency data available');
    }

    // Transform CoinGecko data to our format
    return data.map((coin) => ({
      id: coin.id, // CoinGecko ID (e.g., 'bitcoin', 'ethereum')
      symbol: coin.symbol.toUpperCase(), // Symbol (e.g., 'BTC', 'ETH')
      displaySymbol: coin.symbol.toUpperCase(),
      name: coin.name, // Full name (e.g., 'Bitcoin', 'Ethereum')
      currentPrice: coin.current_price || null,
      priceChangePercent: coin.price_change_percentage_24h || null,
      priceChange: coin.price_change_24h || null, // 24h change amount
      sevenDayChange: coin.price_change_percentage_7d_in_currency || null, // 7-day change percentage
      high24h: coin.high_24h || null,
      low24h: coin.low_24h || null,
      volume24h: coin.total_volume || null,
      marketCap: coin.market_cap || null,
      marketCapRank: coin.market_cap_rank || null,
      image: coin.image || null,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error fetching top cryptocurrencies from CoinGecko:', error);
    throw error;
  }
};

/**
 * Get crypto candlestick data for charts using CoinGecko
 * CoinGecko provides historical price data with different time ranges
 */
export const getCryptoCandles = async (coinId, days = 7) => {
  try {
    // CoinGecko market_chart endpoint returns prices, market caps, and volumes
    // days can be: 1, 7, 14, 30, 90, 180, 365, or 'max'
    const response = await fetch(
      `${COINGECKO_BASE_URL}/coins/${coinId}/market_chart?vs_currency=usd&days=${days}&interval=${days <= 7 ? 'hourly' : 'daily'}`
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('CoinGecko API rate limit exceeded. Please wait a moment and try again.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.prices || !Array.isArray(data.prices) || data.prices.length === 0) {
      throw new Error(`No historical data available for ${coinId}`);
    }

    // CoinGecko returns prices as [timestamp, price] pairs
    // We'll extract close prices and create candle-like structure
    const candles = data.prices.map(([timestamp, price], index) => {
      // Try to get high/low from market_caps if available, otherwise use price
      const high = data.high_24h?.[index]?.[1] || price;
      const low = data.low_24h?.[index]?.[1] || price;
      
      return {
        date: new Date(timestamp).toISOString(),
        timestamp: timestamp,
        open: index > 0 ? data.prices[index - 1][1] : price,
        high: high,
        low: low,
        close: price,
        volume: data.total_volumes?.[index]?.[1] || null
      };
    });

    return candles;
  } catch (error) {
    console.error('CoinGecko candles error:', error);
    throw error;
  }
};

/**
 * Get 7-day price change for a crypto
 * With CoinGecko, this is already included in the market data, but we can calculate it from historical data
 */
export const getCrypto7DayChange = async (coinId) => {
  try {
    // Get 7-day market chart
    const response = await fetch(
      `${COINGECKO_BASE_URL}/coins/${coinId}/market_chart?vs_currency=usd&days=7`
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('CoinGecko API rate limit exceeded. Please wait a moment and try again.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.prices || !Array.isArray(data.prices) || data.prices.length < 2) {
      return null;
    }

    const firstPrice = data.prices[0][1]; // First price
    const lastPrice = data.prices[data.prices.length - 1][1]; // Last price

    if (!firstPrice || !lastPrice) {
      return null;
    }

    const change = lastPrice - firstPrice;
    const changePercent = ((change / firstPrice) * 100);

    return {
      change,
      changePercent,
      firstPrice,
      lastPrice,
      is24hChange: false // This is actual 7-day data
    };
  } catch (error) {
    console.warn(`Failed to get 7-day change for ${coinId}:`, error.message);
    return null;
  }
};

/**
 * Get crypto quote (current price and basic info) by CoinGecko ID
 * This is useful for getting individual crypto data
 */
export const getCryptoQuote = async (coinId) => {
  try {
    const response = await fetch(
      `${COINGECKO_BASE_URL}/simple/price?ids=${coinId}&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true&include_market_cap=true`
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('CoinGecko API rate limit exceeded. Please wait a moment and try again.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data[coinId]) {
      return null;
    }

    const coinData = data[coinId];

    return {
      currentPrice: coinData.usd || null,
      priceChangePercent: coinData.usd_24h_change || null,
      volume24h: coinData.usd_24h_vol || null,
      marketCap: coinData.usd_market_cap || null
    };
  } catch (error) {
    console.warn(`CoinGecko quote error for ${coinId}:`, error.message);
    return null;
  }
};

/**
 * Helper to get CoinGecko ID from symbol (for backward compatibility)
 * Note: CoinGecko uses IDs like 'bitcoin', 'ethereum', not symbols like 'BTC', 'ETH'
 */
export const getCoinGeckoId = (symbol) => {
  // Map common symbols to CoinGecko IDs
  const symbolToId = {
    'BTC': 'bitcoin',
    'ETH': 'ethereum',
    'BNB': 'binancecoin',
    'ADA': 'cardano',
    'SOL': 'solana',
    'XRP': 'ripple',
    'DOT': 'polkadot',
    'DOGE': 'dogecoin',
    'MATIC': 'matic-network',
    'LTC': 'litecoin',
    'AVAX': 'avalanche-2',
    'LINK': 'chainlink',
    'ATOM': 'cosmos',
    'UNI': 'uniswap',
    'ETC': 'ethereum-classic'
  };

  // If symbol contains 'BINANCE:' prefix, extract the base symbol
  const cleanSymbol = symbol.includes(':') 
    ? symbol.split(':')[1].replace('USDT', '').replace('USD', '')
    : symbol.replace('USDT', '').replace('USD', '');

  return symbolToId[cleanSymbol] || cleanSymbol.toLowerCase();
};
